import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, useApp } from "../context/AppContext";
import { scoreLabel } from "../components/common/gapAssessmentTheme";
import { syncModuleJourney } from "../utils/moduleJourney";

function pctFrom(completed, total) {
  const t = Number(total) || 0;
  const c = Number(completed) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((c / t) * 100));
}

function sumStatValues(...vals) {
  return vals.reduce((sum, v) => sum + (Number(v) || 0), 0);
}

function effectiveStatTotal(apiTotal, ...parts) {
  const sum = sumStatValues(...parts);
  const api = Number(apiTotal) || 0;
  return Math.max(api, sum);
}

function deptModulePct(roadmapDeptProgress, moduleKey) {
  const m = roadmapDeptProgress?.[moduleKey];
  if (!m || !m.departments_total) return null;
  if (m.all_complete) return 100;
  return pctFrom(m.departments_complete, m.departments_total);
}

function hasOrgRoadmapData(roadmapDeptProgress) {
  return Boolean(roadmapDeptProgress?.assess?.departments_total);
}

export function computeLifecycleProgress({
  gapStats,
  ropaStats,
  piaStats,
  tprmStats,
  consentStats,
  dsarStats,
  roadmapDeptProgress = null,
}) {
  let assess = deptModulePct(roadmapDeptProgress, "assess");
  if (assess == null) {
    if (!hasOrgRoadmapData(roadmapDeptProgress) && gapStats?.submitted) {
      assess = 100;
    } else if (gapStats) {
      assess =
        pctFrom(gapStats.sectionsCompleted, gapStats.sectionsTotal) ||
        pctFrom(gapStats.answered, gapStats.questionsTotal);
    } else {
      assess = 0;
    }
  }

  let ropa = deptModulePct(roadmapDeptProgress, "ropa");
  if (ropa == null) {
    if (!hasOrgRoadmapData(roadmapDeptProgress) && ropaStats?.status === "submitted") {
      ropa = 100;
    } else if (ropaStats) {
      ropa = ropaStats.progressPct ?? 0;
    } else {
      ropa = 0;
    }
  }

  let pia = deptModulePct(roadmapDeptProgress, "pia");
  if (pia == null) {
    if (!hasOrgRoadmapData(roadmapDeptProgress) && (piaStats?.total ?? 0) > 0) {
      pia = 100;
    } else {
      pia = 0;
    }
  }

  let tprm = deptModulePct(roadmapDeptProgress, "tprm");
  if (tprm == null) {
    const tTotal = tprmStats?.total ?? 0;
    const tSubmitted = tprmStats?.submitted ?? 0;
    if (
      !hasOrgRoadmapData(roadmapDeptProgress) &&
      tTotal > 0 &&
      tSubmitted >= tTotal
    ) {
      tprm = 100;
    } else if (tTotal > 0) {
      tprm = pctFrom(tSubmitted, tTotal);
    } else {
      tprm = 0;
    }
  }

  let consent = 0;
  const consentEvents = sumStatValues(
    consentStats?.accepted,
    consentStats?.declined,
    consentStats?.revoked,
  );
  if (consentEvents > 0 || (consentStats?.total ?? 0) > 0) {
    consent = 100;
  }

  let dsar = 0;
  const dsarProcessed = dsarStats?.processed ?? 0;
  const dsarPending = dsarStats?.pending ?? 0;
  const dsarTotal = dsarStats
    ? effectiveStatTotal(dsarStats.total, dsarProcessed, dsarPending)
    : 0;
  if (dsarTotal > 0) {
    dsar = pctFrom(dsarProcessed, dsarTotal);
    if (dsarProcessed >= dsarTotal) dsar = 100;
  }

  // Breach module is not live yet — stay pending until backend readiness metrics exist.
  const breach = 0;

  let monitor = 0;
  try {
    monitor = localStorage.getItem("onedpdp_reports_visited") === "1" ? 100 : 0;
  } catch {
    // ignore
  }

  return { assess, ropa, pia, tprm, consent, dsar, breach, monitor };
}

function submissionWindow(items) {
  if (!items?.length) return {};
  let first = null;
  let lastComplete = null;
  for (const item of items) {
    const created = item.created_at;
    const done =
      item.submitted_at ||
      item.locked_at ||
      (item.status === "submitted" ? item.updated_at || item.created_at : null);
    if (created && (!first || new Date(created) < new Date(first)))
      first = created;
    if (done && (!lastComplete || new Date(done) > new Date(lastComplete)))
      lastComplete = done;
  }
  return {
    firstActivityAt: first,
    completedAt: lastComplete,
    lastUsedAt: lastComplete || first,
  };
}

function buildApiTimestamps({
  gapStats,
  ropaStats,
  piaStats,
  tprmStats,
  consentStats,
  dsarStats,
  progress,
  submissionMeta = {},
  roadmapDeptProgress = null,
}) {
  let breachVisited = null;
  let reportsVisited = null;
  try {
    reportsVisited =
      progress.monitor >= 100
        ? localStorage.getItem("onedpdp_reports_visited_at")
        : null;
  } catch {
    // ignore
  }

  return {
    assess: {
      ...submissionMeta.assess,
      lastUsedAt: submissionMeta.assess?.lastUsedAt || gapStats?.lastUsedAt,
      completedAt:
        roadmapDeptProgress?.assess?.all_complete ||
        (!hasOrgRoadmapData(roadmapDeptProgress) && gapStats?.submitted)
          ? submissionMeta.assess?.completedAt || gapStats?.lastUsedAt
          : null,
      firstActivityAt:
        submissionMeta.assess?.firstActivityAt || gapStats?.lastUsedAt,
    },
    ropa: {
      ...submissionMeta.ropa,
      lastUsedAt: submissionMeta.ropa?.lastUsedAt || ropaStats?.lastUsedAt,
      completedAt:
        roadmapDeptProgress?.ropa?.all_complete ||
        (!hasOrgRoadmapData(roadmapDeptProgress) &&
          ropaStats?.status === "submitted")
          ? submissionMeta.ropa?.completedAt || ropaStats?.lastUsedAt
          : null,
      firstActivityAt:
        submissionMeta.ropa?.firstActivityAt || ropaStats?.lastUsedAt,
    },
    pia: {
      ...submissionMeta.pia,
      lastUsedAt: submissionMeta.pia?.lastUsedAt || piaStats?.lastUsedAt,
      completedAt:
        roadmapDeptProgress?.pia?.all_complete ||
        (!hasOrgRoadmapData(roadmapDeptProgress) && (piaStats?.total ?? 0) > 0)
          ? submissionMeta.pia?.completedAt || piaStats?.lastUsedAt
          : null,
      firstActivityAt:
        submissionMeta.pia?.firstActivityAt || piaStats?.lastUsedAt,
    },
    tprm: {
      ...submissionMeta.tprm,
      lastUsedAt: submissionMeta.tprm?.lastUsedAt || tprmStats?.lastUsedAt,
      completedAt:
        roadmapDeptProgress?.tprm?.all_complete ||
        (!hasOrgRoadmapData(roadmapDeptProgress) &&
          tprmStats?.total > 0 &&
          tprmStats?.submitted >= tprmStats?.total)
          ? submissionMeta.tprm?.completedAt || tprmStats?.lastUsedAt
          : null,
      firstActivityAt:
        submissionMeta.tprm?.firstActivityAt || tprmStats?.lastUsedAt,
    },
    consent: {
      lastUsedAt: consentStats?.updated_at,
      completedAt: progress.consent >= 100 ? consentStats?.updated_at : null,
      firstActivityAt: consentStats?.updated_at,
    },
    dsar: {
      lastUsedAt: dsarStats?.updated_at,
      completedAt: progress.dsar >= 100 ? dsarStats?.updated_at : null,
      firstActivityAt: dsarStats?.updated_at,
    },
    breach: {
      lastUsedAt: breachVisited,
      completedAt: breachVisited,
      firstActivityAt: breachVisited,
    },
    monitor: {
      lastUsedAt: reportsVisited,
      completedAt: reportsVisited,
      firstActivityAt: reportsVisited,
    },
  };
}

export function overallLifecycleScore(progress) {
  const vals = Object.values(progress || {});
  if (!vals.length) return null;
  return Math.round(
    vals.reduce((a, b) => a + (Number(b) || 0), 0) / vals.length,
  );
}

export default function useComplianceLifecycle() {
  const { currentUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [gapStats, setGapStats] = useState(null);
  const [ropaStats, setRopaStats] = useState(null);
  const [piaStats, setPiaStats] = useState(null);
  const [tprmStats, setTprmStats] = useState(null);
  const [consentStats, setConsentStats] = useState(null);
  const [dsarStats, setDsarStats] = useState(null);
  const [submissionMeta, setSubmissionMeta] = useState({});
  const [roadmapDeptProgress, setRoadmapDeptProgress] = useState(null);

  const userKey = currentUser?.id ?? currentUser?.email ?? "default";

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [
        overviewRes,
        gapRes,
        ropaRes,
        piaRes,
        tprmRes,
        consentRes,
        dsarRes,
        roadmapRes,
        gapSubRes,
        ropaSubRes,
        piaSubRes,
        tprmSubRes,
      ] = await Promise.all([
        apiFetch("/dashboard/overview"),
        apiFetch("/gap-assessment/stats"),
        apiFetch("/ropa/stats"),
        apiFetch("/pia/stats"),
        apiFetch("/tprm/stats"),
        apiFetch("/consent/stats"),
        apiFetch("/dsar/stats"),
        apiFetch("/dashboard/roadmap-progress"),
        apiFetch("/gap-assessment/submissions?page=1&page_size=20"),
        apiFetch("/ropa/submissions?page=1&page_size=20"),
        apiFetch("/pia/submissions?page=1&page_size=20"),
        apiFetch("/tprm/submissions?page=1&page_size=20"),
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (gapRes.ok) {
        const data = await gapRes.json();
        setGapStats({
          sectionsTotal: data.sections_total ?? 0,
          sectionsCompleted: data.sections_completed ?? 0,
          answered: data.answered ?? 0,
          questionsTotal: data.questions_total ?? 0,
          submitted: data.submitted ?? false,
          score: data.score ?? null,
          complianceLabel: data.compliance_label ?? null,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
      if (ropaRes.ok) {
        const data = await ropaRes.json();
        setRopaStats({
          progressPct: data.progress_pct ?? 0,
          status: data.status ?? "not_started",
          submitted: data.submitted ?? 0,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
      if (piaRes.ok) {
        const data = await piaRes.json();
        setPiaStats({
          total: data.total ?? 0,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
      if (tprmRes.ok) {
        const data = await tprmRes.json();
        setTprmStats({
          total: data.total ?? 0,
          submitted: data.submitted ?? 0,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
      if (consentRes.ok) setConsentStats(await consentRes.json());
      if (dsarRes.ok) setDsarStats(await dsarRes.json());
      if (roadmapRes.ok) {
        const data = await roadmapRes.json();
        setRoadmapDeptProgress({
          assess: data.assess,
          ropa: data.ropa,
          pia: data.pia,
          tprm: data.tprm,
          departments_total: data.departments_total,
          users_total: data.users_total,
        });
      }

      const meta = {};
      if (gapSubRes.ok) {
        const data = await gapSubRes.json();
        meta.assess = submissionWindow(data.submissions || []);
      }
      if (ropaSubRes.ok) {
        const data = await ropaSubRes.json();
        meta.ropa = submissionWindow(data.submissions || data.items || []);
      }
      if (piaSubRes.ok) {
        const data = await piaSubRes.json();
        meta.pia = submissionWindow(data.submissions || data.items || []);
      }
      if (tprmSubRes.ok) {
        const data = await tprmSubRes.json();
        meta.tprm = submissionWindow(data.submissions || data.items || []);
      }
      setSubmissionMeta(meta);
    } catch {
      // keep prior values
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  useEffect(() => {
    const onFocus = () => fetchAll(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchAll]);

  const progress = useMemo(
    () =>
      computeLifecycleProgress({
        gapStats,
        ropaStats,
        piaStats,
        tprmStats,
        consentStats,
        dsarStats,
        roadmapDeptProgress,
      }),
    [
      gapStats,
      ropaStats,
      piaStats,
      tprmStats,
      consentStats,
      dsarStats,
      roadmapDeptProgress,
    ],
  );

  const timeline = useMemo(() => {
    const apiTimestamps = buildApiTimestamps({
      gapStats,
      ropaStats,
      piaStats,
      tprmStats,
      consentStats,
      dsarStats,
      progress,
      submissionMeta,
      roadmapDeptProgress,
    });
    return syncModuleJourney({ progress, apiTimestamps, userKey });
  }, [
    progress,
    gapStats,
    ropaStats,
    piaStats,
    tprmStats,
    consentStats,
    dsarStats,
    submissionMeta,
    userKey,
    roadmapDeptProgress,
  ]);

  const lifecycleScore = useMemo(
    () => overallLifecycleScore(progress),
    [progress],
  );

  const gapScore = gapStats?.score ?? overview?.compliance_score ?? null;
  const complianceScore = lifecycleScore ?? gapScore;
  const complianceLabel =
    lifecycleScore != null
      ? scoreLabel(lifecycleScore)
      : (gapStats?.complianceLabel ??
        overview?.compliance_label ??
        (gapScore != null ? scoreLabel(gapScore) : null));

  const orgName =
    overview?.organization_name ||
    currentUser?.organization_name ||
    "Your Organization";

  const completedCount = useMemo(
    () => Object.values(progress).filter((p) => p >= 100).length,
    [progress],
  );

  const totalDurationMs = useMemo(
    () =>
      timeline
        .filter((t) => t.status === "complete" && t.durationMs != null)
        .reduce((sum, t) => sum + t.durationMs, 0),
    [timeline],
  );

  return {
    loading,
    refreshing,
    refresh: () => fetchAll(true),
    progress,
    timeline,
    complianceScore,
    complianceLabel,
    orgName,
    completedCount,
    totalStages: 8,
    totalDurationMs,
    roadmapDeptProgress,
    activeUsers: overview?.active_users ?? 0,
    organizationCreatedAt: overview?.organization_created_at || null,
  };
}
